import { useSearchParams } from 'react-router-dom'
import { leadStatsViewSchema } from '@lib/enums/lead-stats-view.enum'
import { pipelineComparisonSchema, pipelineSpanSchema, type PipelineComparison, type PipelineSpan } from '@lib/enums/pipeline-span.enum'
import { pipelineSplitSchema, type PipelineQuery } from '@lib/input/pipeline.input'
import { calendarDaySchema } from '@lib/validators/temporal.validator'

/**
 * Where a Pipeline page opens (docs/pipeline-view.spec.md, "Spans"): twelve
 * weeks, against the same weeks last year. Two weeks at one venue is a handful
 * of couples, and weddings are seasonal, so the weeks before say little.
 */
export const PIPELINE_PAGE_SPAN: PipelineSpan = 'twelve_weeks'
export const PIPELINE_PAGE_COMPARE: PipelineComparison = 'last_year'

/** The query a Pipeline page asks for: always a span and a comparison, the page's own when the address names none. */
export type PipelinePageQuery = PipelineQuery & { span: PipelineSpan; compare: PipelineComparison }

/**
 * A Pipeline page's split, view, weeks and comparison live in its address, so a
 * link reopens them. The page's defaults stay out of the address.
 */
export function usePipelineQuery(): [PipelinePageQuery, (next: PipelinePageQuery) => void] {
  const [params, setParams] = useSearchParams()
  const split = pipelineSplitSchema.safeParse(params.get('split'))
  const view = leadStatsViewSchema.safeParse(params.get('view'))
  const period = calendarDaySchema.safeParse(params.get('period'))
  const span = pipelineSpanSchema.safeParse(params.get('span'))
  const compare = pipelineComparisonSchema.safeParse(params.get('compare'))
  const query: PipelinePageQuery = {
    ...(split.success ? { split: split.data } : {}),
    ...(view.success ? { view: view.data } : {}),
    ...(period.success ? { period: period.data } : {}),
    span: span.success ? span.data : PIPELINE_PAGE_SPAN,
    compare: compare.success ? compare.data : PIPELINE_PAGE_COMPARE,
  }
  function set(next: PipelinePageQuery): void {
    const values: Record<'split' | 'view' | 'period' | 'span' | 'compare', string | undefined> = {
      split: next.split === 'all' ? undefined : next.split,
      view: next.view === 'cohort' ? undefined : next.view,
      period: next.period,
      span: next.span === PIPELINE_PAGE_SPAN ? undefined : next.span,
      compare: next.compare === PIPELINE_PAGE_COMPARE ? undefined : next.compare,
    }
    setParams(
      (current) => {
        const out = new URLSearchParams(current)
        for (const [key, value] of Object.entries(values)) {
          if (value === undefined) out.delete(key)
          else out.set(key, value)
        }
        return out
      },
      { replace: true },
    )
  }
  return [query, set]
}
